import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

enum LogType {
  Log = 0,
  Error = 1,
  Warn = 2,
  Table = 3,
  Dir = 4,
}

export enum LogAppSection {
  Analytics = 'analytics',
  StoryShowing = 'storyShowing',
  StoryCreating = 'storyCreating',
}

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  /**
   * Logging your data in the console
   * @param message Data for logging
   * @param optionalParams Additional data (e.g. { appSection: LogAppSection.StoryCreating })
   */
  log(message?: any, ...optionalParams: any[]): void {
    this.writeToLog(message, LogType.Log, ...optionalParams);
  }

  /**
   * Logging your error data in the console
   * @param message Data for logging
   * @param optionalParams Additional data (e.g. { appSection: LogAppSection.StoryCreating })
   */
  error(message?: any, ...optionalParams: any[]): void {
    this.writeToLog(message, LogType.Error, ...optionalParams);
  }

  /**
   * Logging your warning data in the console
   * @param message Data for logging
   * @param optionalParams Additional data (e.g. { appSection: LogAppSection.StoryCreating })
   */
  warn(message?: any, ...optionalParams: any[]): void {
    this.writeToLog(message, LogType.Warn, ...optionalParams);
  }

  /**
   * Logging your tabular data as a table in the console
   * @param message Data for logging
   * @param optionalParams Additional data (e.g. { appSection: LogAppSection.StoryCreating })
   */
  table(message?: any, ...optionalParams: any[]): void {
    this.writeToLog(message, LogType.Table, ...optionalParams);
  }

  /**
   * Logging an interactive list of the properties of the specified object in the console
   * @param message Data for logging
   * @param optionalParams Additional data (e.g. { appSection: LogAppSection.StoryCreating })
   */
  dir(obj?: any, ...optionalParams: any[]): void {
    this.writeToLog(obj, LogType.Dir, ...optionalParams);
  }

  private loggingEnabled(logAppSection: LogAppSection): boolean {
    return (
      !environment.production &&
      (!logAppSection || (logAppSection && environment.logging[logAppSection]))
    );
  }

  private writeToLog(
    message?: any,
    logType?: LogType,
    ...optionalParams: any[]
  ): void {
    const configParams = optionalParams.find((item: any) => item.appSection);
    if (this.loggingEnabled(configParams ? configParams.appSection : null)) {
      let optParams = optionalParams;
      if (configParams) {
        optParams = optionalParams.filter((item: any) => !item.appSection);
      }

      switch (logType) {
        case LogType.Error:
          console.error(message, ...optParams);
          break;
        case LogType.Warn:
          console.warn(message, ...optParams);
          break;
        case LogType.Table:
          console.table(message, ...optParams);
          break;
        case LogType.Dir:
          console.dir(message, ...optParams);
          break;
        default:
          console.log(message, ...optParams);
      }
    }
  }
}
